import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import CloseIcon from '@mui/icons-material/Close';

export type ModalType = 'normal' | 'success' | 'danger';

export interface ModalProps {
  open: boolean;
  title?: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  onClose: () => void;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hideCloseButton?: boolean;
  type?: ModalType;
  onlyCloseIcon?: boolean; 
}

const typeStyles = {
  normal: {
    color: '#222',
    iconBg: '#EEF0FF',
    iconColor: '#2D4BFF',
  },
  success: {
    color: '#388e3c',
    iconBg: '#E6F4EA',
    iconColor: '#43a047',
  },
  danger: {
    color: '#d32f2f',
    iconBg: '#FDEAEA',
    iconColor: '#d32f2f',
  },
};

const IconBox = styled(Box)<{ bgcolor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bgcolor }) => bgcolor};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
`;

function getIcon(type: ModalType) {
  if (type === 'success') {
    return <CheckCircleOutlinedIcon sx={{ fontSize: 32, color: typeStyles.success.iconColor }} />;
  }
  if (type === 'danger') {
    return <HighlightOffOutlinedIcon sx={{ fontSize: 32, color: typeStyles.danger.iconColor }} />;
  }
 
  return <InfoOutlinedIcon sx={{ fontSize: 32, color: typeStyles.normal.iconColor }} />;
}

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
  actions,
  maxWidth = 'sm',
  hideCloseButton = false,
  type = 'normal',
  onlyCloseIcon = false,
}: ModalProps) {
  const style = typeStyles[type];

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
     
      <Box position="absolute" top={16} right={16} zIndex={10}>
        <Button onClick={onClose} sx={{ minWidth: 0, p: 1 }} color="inherit">
          <CloseIcon />
        </Button>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
        <IconBox bgcolor={style.iconBg}>
          {getIcon(type)}
        </IconBox>
        {title && (
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: style.color,
              fontWeight: 700,
              fontSize: 22,
              padding: 0,
              marginBottom: 1,
              background: 'none',
            }}
          >
            {title}
          </DialogTitle>
        )}
      </Box>
      <DialogContent sx={{ textAlign: 'center', color: style.color }}>
        {description && (
          <Typography variant="body1" color="inherit" mb={2}>
            {description}
          </Typography>
        )}
        {children}
      </DialogContent>
      {!onlyCloseIcon && (
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          {actions ? (
            actions
          ) : !hideCloseButton ? (
            <Button onClick={onClose} color="primary" variant="contained">
              Fechar
            </Button>
          ) : null}
        </DialogActions>
      )}
    </Dialog>
  );
}